import { useEffect, useState } from 'react';
import { GripVertical, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { fetchWorkRoles, putWorkRoles } from '@/lib/api/work-plan';
// The slug rule is shared with the staff-flags editor (same server derivation for both).
import { slugFromLabel } from '@/lib/work-plan-flags';

// Editor for the work-role vocabulary (global settings, org-wide): the roster rail's role
// pills and the values the employee editor offers. Ids are slugs derived from the label on
// first save and then FROZEN — they are stored on user documents, so renaming a role keeps
// everyone's assignment while retyping an id would orphan it.

const DEFAULT_SUGGESTIONS = ['Admin', 'Optom', 'Trainee'];

function SortableRoleRow({ row, index, canEdit, onLabelChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.key,
    disabled: !canEdit,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-lg border border-border bg-card px-2 py-1.5',
        isDragging && 'opacity-60 shadow-md',
      )}
    >
      {canEdit ? (
        <button
          type="button"
          className="flex size-6 shrink-0 cursor-grab items-center justify-center rounded text-muted-foreground hover:bg-muted"
          title="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-3.5" />
        </button>
      ) : (
        <span className="size-6 shrink-0" />
      )}
      <Input
        value={row.label}
        onChange={(event) => onLabelChange(index, event.target.value)}
        placeholder="Role name"
        disabled={!canEdit}
        maxLength={40}
        className="h-8 flex-1 text-sm"
        aria-label={`Work role ${index + 1} name`}
      />
      <code
        className="hidden w-32 shrink-0 truncate rounded bg-muted px-1.5 py-1 font-mono text-[10.5px] text-muted-foreground sm:block"
        title={row.id ? `Stored id: ${row.id}` : 'Id is generated from the name when you save'}
      >
        {row.id || slugFromLabel(row.label) || '—'}
      </code>
      {canEdit ? (
        <button
          type="button"
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40"
          title="Remove this role"
          onClick={() => onRemove(index)}
        >
          <Trash2 className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export function WorkRolesEditor({ canEdit = false }) {
  const [rows, setRows] = useState([]);
  const [baseline, setBaseline] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Local keys keep dnd-kit and React stable while a brand-new row has no server id yet.
  const [keySeq, setKeySeq] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const toRows = (roles, startSeq) =>
    (roles || []).map((role, i) => ({ key: `r${startSeq + i}`, id: role.id || '', label: role.label || role.id || '' }));

  useEffect(() => {
    let cancelled = false;
    fetchWorkRoles()
      .then((res) => {
        if (cancelled) return;
        const roles = Array.isArray(res?.roles) ? res.roles : [];
        setRows(toRows(roles, 0));
        setBaseline(roles.map((role) => ({ id: role.id, label: role.label })));
        setKeySeq(roles.length);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setIsLoading(false);
        setLoadFailed(true);
        toast.error(error instanceof Error ? error.message : 'Could not load work roles.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isDirty =
    rows.length !== baseline.length ||
    rows.some((row, i) => row.id !== baseline[i]?.id || row.label.trim() !== baseline[i]?.label);

  const addRow = (label = '') => {
    setRows((current) => [...current, { key: `r${keySeq}`, id: '', label }]);
    setKeySeq((seq) => seq + 1);
  };

  const handleSave = async () => {
    const cleaned = rows.map((row) => ({ id: row.id, label: row.label.trim() })).filter((row) => row.label || row.id);
    // Blank names would be saved as their slug id — catch it here with a clear message.
    if (cleaned.some((row) => !row.label)) {
      toast.error('Every role needs a name.');
      return;
    }
    const slugs = cleaned.map((row) => row.id || slugFromLabel(row.label));
    if (slugs.some((slug) => !slug)) {
      toast.error('Role names must contain at least one letter or number.');
      return;
    }
    if (new Set(slugs).size !== slugs.length) {
      toast.error('Two roles resolve to the same id — give them more distinct names.');
      return;
    }
    setIsSaving(true);
    try {
      const res = await putWorkRoles({ roles: cleaned });
      const saved = Array.isArray(res?.roles) ? res.roles : [];
      setRows(toRows(saved, keySeq));
      setKeySeq((seq) => seq + saved.length);
      setBaseline(saved.map((role) => ({ id: role.id, label: role.label })));
      toast.success('Work roles saved for everyone.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save work roles.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setRows(toRows(baseline, keySeq));
    setKeySeq((seq) => seq + baseline.length);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Loading work roles…
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {loadFailed ? (
        <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          Work roles could not be loaded. Saving now would overwrite the stored list — reload before editing.
        </p>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={({ active, over }) => {
            if (!over || active.id === over.id) return;
            setRows((current) => {
              const from = current.findIndex((row) => row.key === active.id);
              const to = current.findIndex((row) => row.key === over.id);
              if (from < 0 || to < 0) return current;
              const next = [...current];
              const [moved] = next.splice(from, 1);
              next.splice(to, 0, moved);
              return next;
            });
          }}
        >
          <SortableContext items={rows.map((row) => row.key)} strategy={verticalListSortingStrategy}>
            {rows.map((row, index) => (
              <SortableRoleRow
                key={row.key}
                row={row}
                index={index}
                canEdit={canEdit}
                onLabelChange={(i, label) =>
                  setRows((current) => current.map((r, ri) => (ri === i ? { ...r, label } : r)))
                }
                onRemove={(i) => setRows((current) => current.filter((_, ri) => ri !== i))}
              />
            ))}
          </SortableContext>
        </DndContext>

        {rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
            No work roles configured. Staff keep whatever role they were given, but the roster rail shows no
            filter pills.
            {canEdit ? (
              <span className="mt-2 flex flex-wrap justify-center gap-1.5">
                {DEFAULT_SUGGESTIONS.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-muted/70"
                    onClick={() => addRow(label)}
                  >
                    + {label}
                  </button>
                ))}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="[&_svg]:size-3.5" onClick={() => addRow()}>
            <Plus />
            Add role
          </Button>
          <span className="flex-1" />
          {isDirty ? (
            <Button type="button" variant="ghost" size="sm" className="[&_svg]:size-3.5" onClick={handleReset} disabled={isSaving}>
              <RotateCcw />
              Discard changes
            </Button>
          ) : null}
          <Button type="button" size="sm" onClick={handleSave} disabled={!isDirty || isSaving}>
            {isSaving ? 'Saving…' : 'Save work roles'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          You need the scheduling <span className="font-semibold">manage</span> permission to edit these.
        </p>
      )}

      <p className="text-[11.5px] leading-relaxed text-muted-foreground">
        The <span className="font-semibold">first</span> role is the default: employees with no role of their own
        appear under it in the Work Plan roster. Renaming a role keeps everyone assigned to it — the stored id
        (shown on the right) never changes once saved. Removing a role leaves the people who had it without a
        pill until you give them a new one.
      </p>
    </div>
  );
}
